// English UI messages — the source of truth for the Messages shape.
// Every other locale must satisfy `Messages` so a missing key is a type error.
export const en = {
  common: {
    cancel: 'Cancel',
    read: 'Read',
    remove: 'Remove',
    save: 'Save',
    saving: 'Saving…',
    close: 'Close',
    goHome: 'Go Home',
    error: (message: string) => `Error: ${message}`,
  },

  shelf: {
    unableToLoadBooks: 'Unable to load books',
    signIn: 'Sign in',
    logout: 'Logout',
    credits: 'Credits',
    buyCredits: 'Buy Credits',
    help: 'Help',
    dockHint: 'Move to look around · Scroll to zoom · Click to open',
    prevPage: 'Previous shelf page',
    nextPage: 'Next shelf page',
    byAuthor: (author: string) => `By ${author}`,
    removeBook: 'Remove Book',
    confirmRemove: 'Are you sure you want to remove this book?',
    translationFailed: 'Translation failed',
    translating: 'Translating...',
    translatingProgress: (done: number, total: number, pct: number) =>
      `Translating... ${done}/${total} chapters (${pct}%)`,
    extractingGlossary: 'Extracting glossary...',
    preparingArtwork: 'Preparing cover and spine...',
    safeToClose:
      'You can safely close this page — the book will be ready when you return.',
    completed: '✓ Completed',
    percentRead: (pct: number) => `${pct}% read`,
    notStarted: 'Not started',
    uploadedTitle: 'Book uploaded!',
    uploadedOriginalOnly: 'Preparing cover and spine — original language only.',
    uploadedTranslating:
      'Translation is in progress — you can safely close this page.',
  },

  closet: {
    loadingQuips: [
      'Sweeping the dust off the shelves…',
      'Straightening the spines…',
      'Lighting the reading lamp…',
      'Waking the librarian…',
    ],
    bookInfo: 'Book Info',
    formLabelNo: 'Form 3',
    formLabelKind: 'Shelf Label',
    labelField: 'label',
    labelPlaceholder: 'e.g. Sci-fi, To read…',
    labelHint: 'this slip clips onto the shelf edge',
    editShelfLabel: 'Edit shelf label',
    titleField: 'title',
    authorField: 'author',
    statusField: 'status',
    progressField: 'progress',
    translatingCount: (done: number, total: number) =>
      `Translating… ${done}/${total}`,
    extractingGlossary: 'Extracting glossary…',
    translating: 'Translating…',
    preparingArtwork: 'Preparing cover and spine…',
    completedStamp: 'Completed',
    shelfTheme: 'Shelf color',
    shelfThemeNames: {
      walnut: 'Walnut',
      white: 'Whitewash',
      steel: 'Steel',
    } as Record<string, string>,
  },

  upload: {
    dialogUpload: 'Upload book',
    dialogAddHere: 'Add book here',
    dialogConfirm: 'Confirm book upload',
    form1No: 'Form 1',
    form1Kind: 'Acquisition',
    form2No: 'Form 2',
    form2Kind: 'Catalogue',
    loginRequired: 'Please login to upload books',
    estimateFailed: 'Failed to estimate book',
    reEstimateFailed: 'Failed to re-estimate',
    uploading: 'Uploading...',
    startingTranslation: 'Starting translation...',
    analyzing: 'analyzing book…',
    uploadFailed: 'Upload failed',
    networkError:
      'Network error while uploading. Please retry. If it keeps failing, contact support with time and browser.',
    insufficientCredits: 'Insufficient credits',
    requiredField: 'required',
    availableField: 'available',
    needField: 'need',
    moreAmount: (amount: string) => `${amount} more`,
    needMore: (amount: string) => `need ${amount} more`,
    balanceField: 'balance',
    afterField: 'after',
    creditsUnit: 'credits',
    shelfField: 'shelf',
    selectedShelf: 'Selected shelf',
    modeAria: 'Upload mode',
    translateThisBook: 'Translate this book',
    bilingualNote: 'bilingual',
    importOriginalOnly: 'Import original only',
    freeNote: 'free',
    languagesField: 'languages',
    sourceLanguage: 'Source language',
    targetLanguage: 'Target language',
    sameLanguageWarning:
      'source and target language are the same — change one to continue',
    confirmTranslate: 'Confirm & Translate',
    importWithoutTranslation: 'Import without Translation',
    affixHere: 'affix epub here',
    languagesNextForm: 'languages chosen on the next form',
  },

  creditsModal: {
    title: 'Buy Credits',
    dialogAria: 'Buy credits',
    balancePrefix: 'Current balance:',
    creditsSuffix: 'credits',
    packageUnit: 'credits',
    info: 'Credits are used for book translations.',
  },

  reader: {
    loadingChapter: 'Loading chapter...',
    prevChapter: 'Previous Chapter',
    nextChapter: 'Next Chapter',
    menu: 'Menu',
    tapToToggle: 'Tap to toggle translation',
    note: 'Note',
    noteNotFound: 'Note content not found',
    viewInContext: 'View in context',
    returnToReading: 'Return to reading',
    backToShelf: 'Back to Shelf',
    showTranslation: 'Show Translation',
    showOriginal: 'Show Original',
    chapters: 'Chapters',
    markAsRead: 'Mark as Read',
    markedRead: '✓ Read',
    share: 'Share',
    copyShareLink: 'Copy Share Link',
    copied: '✓ Copied!',
    revokeShare: 'Revoke Share',
    theme: 'Theme',
    themeAuto: 'Auto (follow system)',
    themeNames: {
      white: 'White',
      paper: 'Paper',
      sepia: 'Sepia',
      green: 'Green',
      dark: 'Dark',
    } as Record<string, string>,
    firstLineIndent: 'First-line Indent',
    typography: 'Typography',
    font: 'Font',
    fontNames: {
      song: 'Song',
      kai: 'Kai',
      sans: 'Sans',
    } as Record<string, string>,
    fontSize: 'Font Size',
    paragraphGap: 'Paragraph Gap',
    lineHeight: 'Line Height',
    letterSpacing: 'Letter Spacing',
    wordSpacing: 'Word Spacing',
    fontWeight: 'Font Weight',
    resetToDefault: 'Reset to Default',
    contents: 'Contents',
    search: 'Search',
    searchPlaceholder: 'Search original & translation…',
    searching: 'Searching…',
    searchNoResults: 'No matches found',
  },

  auth: {
    continueWithGoogle: 'Continue with Google',
    emailPlaceholder: 'you@example.com',
    sendCode: 'Email me a sign-in code',
    orEmail: 'or continue with email',
    codeSentLabel: 'Code sent to',
    codePlaceholder: '6-digit code',
    verifyAndSignIn: 'Sign in',
    changeEmail: 'Use a different email',
    sendCodeFailed: 'Failed to send the code',
    verifyFailed: 'Sign-in failed',
  },

  book: {
    loadingContent: 'Loading book content...',
    loadChapterFailed: 'Failed to load chapter',
    createShareFailed: 'Failed to create share link',
    revokeShareFailed: 'Failed to revoke share',
  },

  shared: {
    linkInvalid: 'This shared link is no longer valid.',
    loadBookFailed: 'Failed to load book.',
    loadChapterFailed: 'Failed to load chapter.',
    goToLibrary: 'Go to Library',
    loadingSharedBook: 'Loading shared book...',
  },

  errors: {
    somethingWentWrong: 'Something went wrong',
    unexpectedError: 'An unexpected error occurred. Please try again.',
    tryAgain: 'Try Again',
    fetchBooksFailed: 'Failed to fetch books',
    deleteBookFailed: 'Failed to delete book',
    saveLabelFailed: 'Failed to save label',
    moveBookFailed: 'Failed to move book',
  },

  pwa: {
    newVersion: 'A new version is available',
    refresh: 'Refresh',
    updating: 'Updating…',
  },

  // Display names for translation source/target languages (keys are the
  // codes in SUPPORTED_LANGUAGES).
  languageNames: {
    zh: 'Chinese',
    es: 'Spanish',
    fr: 'French',
    de: 'German',
    ja: 'Japanese',
    ko: 'Korean',
    ru: 'Russian',
    en: 'English',
  } as Record<string, string>,
};

export type Messages = typeof en;
